<template>
    <div class="DashboardHomeView">
        <WidthWrapper set-vars-on-parent />

        <div class="section section--gradient">
            <MemoransierGamesSection />
        </div>

        <div class="section section--gradient" :class="{ disabled: gettingStartedHasFocus }">
            <BeltSection />
        </div>

        <div class="section section--gradient">
            <GettingStartedSection />
        </div>

        <div v-if="isAdmin" class="section section--gradient">
            <TeamChallengeAdminDashboard v-for="team in teams" :key="team.id" :team="team" :team-goal="teamGoal" />
        </div>

        <div v-if="!isAdmin && teamGoal" class="section section--gradient">
            <TeamChallengeUserView :team-goal="teamGoal" />
        </div>

        <div class="section section--gradient" :class="{ disabled: gettingStartedHasFocus }">
            <IndependentCoursesSection class="Section" />

            <InProgressSection class="Section" />

            <YourInterestsSection class="Section" />

            <RecommendedSection class="Section" />
        </div>

        <div class="section section--gradient" :class="{ disabled: gettingStartedHasFocus }">
            <DashboardFunctionalBraintrainingSection class="functionBrainTraining" />
        </div>

        <div class="section section--darker" :class="{ disabled: gettingStartedHasFocus }">
            <div class="tabs">
                <div
                    class="Tab --global-clickable"
                    :class="{ active: bottomSectionActiveTab === 'collections' }"
                    @click="bottomSectionActiveTab = 'collections'"
                >
                    {{ $t('collections.title') }}
                </div>
                <div
                    class="Tab --global-clickable"
                    :class="{ active: bottomSectionActiveTab === 'categories' }"
                    @click="bottomSectionActiveTab = 'categories'"
                >
                    {{ $t('categories.title') }}
                </div>
            </div>

            <template v-if="bottomSectionActiveTab === 'collections'">
                <div class="text">
                    {{ $t('collections.text') }}
                </div>
                <LifeskillsSection
                    class="Section"
                    :title="$t('future.title')"
                    :text="$t('future.text')"
                    :xshow-numbershape="false"
                    :lifeskill-ids="futureLifeskillIds"
                />

                <LifeskillsSection
                    class="Section"
                    :title="$t('selfmanagement.title')"
                    :text="$t('selfmanagement.text')"
                    :xshow-numbershape="false"
                    :lifeskill-ids="selfmanagementLifeskillIds"
                />

                <LifeskillsSection
                    class="Section"
                    :title="$t('allroundknowledge.title')"
                    :text="$t('allroundknowledge.text')"
                    :xshow-numbershape="false"
                    :lifeskill-ids="allroundknowledgeLifeskillIds"
                />
            </template>
            <template v-else>
                <div class="text">
                    {{ $t('categories.text') }}
                </div>

                <LifeskillsSection
                    v-for="category of categories"
                    :key="category.categoryId"
                    class="Section"
                    :xshow-numbershape="false"
                    :title="category.title"
                    :lifeskill-ids="category.lifeskillIds"
                />
            </template>
        </div>
    </div>
</template>

<translations>
  future:
    title: 'Future Skills'
    title_no: 'Fremtidsferdigheter'
    text: 'Stay ahead of change with the techniques that train your brain to a better day and a safer future'
    text_no: 'Topptrent hjerne i dag - lynrask læring for i morgen'

  allroundknowledge:
    title: 'Allround Knowledge'
    title_no: 'Allmennkunnskap'
    text: 'Stay ahead of change with the insight we all should have'
    text_no: 'Fyll hjernen med klassisk innsikt og nye tanker'

  selfmanagement:
    title: 'Self-management'
    title_no: 'Selvledelse'
    text: 'Train your brain, become a star'
    text_no: 'Tren hjernen så du blir stjernen'

  collections:
    title: 'Collections'
    title_no: 'Samlinger'
    text: 'Explore the skills for a better today and tomorrow - with theory, practice and videos'
    text_no: 'Utforsk ferdighetene for en bedre hverdag og fremtid - med teori, praksis og video'

  categories:
    title: 'Categories'
    title_no: 'Kategorier'
    text: 'Explore Memolife''s ecosystem: 20/100 skills with theory & practice - the rest w/video'
    text_no: 'Utforsk Memolifes økosystem: 20/100 ferdigheter med teori & praksis - resten video'

  viewBy: 'View by'
  viewBy_no: 'Sorter som'
</translations>

<script>
import { mapGetters } from 'vuex';

import { getLifeskillRangeFromCategoryId } from '@/utils';

import WidthWrapper from '../components/WidthWrapper';
import DashboardFunctionalBraintrainingSection from '../components/DashboardFunctionalBraintrainingSection';

import MemoransierGamesSection from '../components/MemoransierGamesSection';
import IndependentCoursesSection from '../components/IndependentCoursesSection';
import GettingStartedSection from '../components/GettingStartedSection';
import InProgressSection from '../components/InProgressSection';
import RecommendedSection from '../components/RecommendedSection';
import YourInterestsSection from '../components/YourInterestsSection';
import LifeskillsSection from '../components/LifeskillsSection';
import TeamChallengeAdminDashboard from '@/modules/dashboard/views/TeamChallenge/TeamChallengeAdminDashboard';
import TeamChallengeUserView from '@/modules/dashboard/views/TeamChallenge/TeamChallengeUserView';

import BeltSection from '../components/BeltSection';

import apolloClient from '@/apollo-client';
import gql from 'graphql-tag';

export default {
    components: {
        WidthWrapper,
        DashboardFunctionalBraintrainingSection,
        MemoransierGamesSection,
        IndependentCoursesSection,
        GettingStartedSection,
        InProgressSection,
        RecommendedSection,
        YourInterestsSection,
        LifeskillsSection,
        BeltSection,
        TeamChallengeAdminDashboard,
        TeamChallengeUserView
    },
    data() {
        return {
            bottomSectionActiveTab: 'collections',
            teamGoals: [],
            teams: []
        };
    },
    computed: {
        ...mapGetters({
            profile: 'moduleAuth/profile'
        }),

        isAdmin() {
            return this.teams.find(team => team.isAdmin);
        },

        lifeskillHideMap() {
            return this.$store.state.moduleApp.lifeskillHideMap;
        },

        futureLifeskillIds() {
            return this.$store.state.moduleApp.packages.essentials.lifeskillIds.filter(
                id => !this.lifeskillHideMap[id]
            );
        },

        allroundknowledgeLifeskillIds() {
            return this.$store.state.moduleApp.packages.allroundknowledge.lifeskillIds.filter(
                id => !this.lifeskillHideMap[id]
            );
        },

        selfmanagementLifeskillIds() {
            return this.$store.state.moduleApp.packages.selfmanagement.lifeskillIds.filter(
                id => !this.lifeskillHideMap[id]
            );
        },

        gettingStartedHasFocus() {
            return this.$store.getters['moduleApp/gettingStartedHasFocus'];
        },

        categories() {
            const categorieIds = Object.keys(this.$store.state.moduleApp.categories);
            return categorieIds.map(categoryId => {
                const lifeskillRange = getLifeskillRangeFromCategoryId(categoryId).join('-');
                return {
                    categoryId,
                    title: this.$t(`dashboard.general.categoryLabels.${categoryId}`) + ' ' + lifeskillRange,
                    lifeskillIds: this.$store.getters['moduleApp/getCategoryById'](categoryId).lifeskills
                };
            });
        },

        teamGoal() {
            return this.teamGoals.filter(teamGoal => {
                const isTeamGoalCompleted =
                    teamGoal.quizGameId &&
                    this.$store.state.moduleMemoransier.games.find(game => game.id === teamGoal.quizGameId)
                        .leaderboardReveal;

                return !isTeamGoalCompleted;
            })[0];
        }
    },

    async mounted() {
        await this.refreshTeamGoals();
        this.refreshTeamGoalsIntervalId = setInterval(async () => {
            await this.refreshTeamGoals();
        }, 5000);
    },

    beforeDestroy() {
        clearInterval(this.refreshTeamGoalsIntervalId);
    },

    beforeRouteEnter(to, from, next) {
        if (from.path === '/vote') {
            next();
            location.reload();
        }

        next();
    },

    methods: {
        async refreshTeamGoals() {
            const response = await apolloClient.query({
                fetchPolicy: 'no-cache',
                query: gql`
                    query {
                        teamGoalsList {
                            id
                            shoutout
                            start
                            stop
                            memoGameId
                            quizGameId
                            teamId
                            journeyId
                            createdBy
                        }
                        teamsList {
                            id
                            name
                            isAdmin
                        }
                    }
                `
            });

            this.teamGoals = response.data.teamGoalsList;
            this.teams = response.data.teamsList;
        }
    }
};
</script>

<style lang="scss" scoped>
.DashboardHomeView {
    overflow-y: auto;
}

.section {
    position: relative;
    padding-left: calc(var(--widthWrapperSideWidth) * 1px);
    padding-right: calc(var(--widthWrapperSideWidth) * 1px);
    padding-top: 2.5em;
    padding-bottom: 2em;

    &:empty {
        border: 1px solid red;
        display: none;
    }

    &.disabled::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(#12254d, 0.8);
    }
}

.section--gradient {
    background: $dashboard-sectionGradient;
}
.section--darker {
    background: linear-gradient(180deg, #0c2d64 0%, #07204b 100%);
}

.Section {
    padding-top: 1.5em;
}

.text {
    color: rgba(white, 0.7);
    line-height: 1.5em;
    margin-top: 1em;
    padding-bottom: 1em;
    border-bottom: 1px solid rgba(white, 0.05); // rgba(133, 222, 185, 0.2);
    margin-bottom: 1em;
}
.tabs {
    border-bottom: 1px solid rgba(white, 0.05); // rgba(133, 222, 185, 0.2);
    display: flex;
}
.Tab {
    padding: 1em 0;

    &.active {
        border-bottom: 3px solid #31d5b7;
        font-weight: 600;
    }

    &:not(:first-child) {
        margin-left: 1.5em;
    }
}
.tabs__label {
    padding: 1em 0;
    color: rgba(white, 0.3);
}
</style>
